/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, TIME_FORMATTER, TimeService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ServiceType } from 'app/common/constants';

export interface ControllerData {
    status?: 'normal' | 'abnormal' | 'noController';
    healthStatus?: string;
    runningStatus?: string;
    usage?: number;
    showUsageWithUnplaceholder?: boolean;
    threshold?: number;
    name?: string;
    id?: string;
    step?: number;
    isAdd?: boolean;
}

/**
 * 时间段范围枚举
 */
export enum TimeSegmentE {
    OneMinutes = 60000,
    FiveMinutes = 300000,
    HalfHour = 1800000,
    OneHour = 3600000,
    OneDay = 86400000,
    OneWeek = 604800000,
    OneMonth = 2592000000,
    OneYear = 31536000000
}

/**
 * 容量趋势时间段范围枚举
 */
export enum CapacityTimeSegmentE {
    All = null,
    OneWeek = 7,
    OneMonth = 30,
    ThreeMonth = 90,
    HalfYear = 180,
    OneYear = 365
}

/**
 * 数据缩减、重删、压缩枚举
 */
export enum DataRedunctionTypeE {
    All = 'all',
    Block = 'block',
    File = 'file'
}

/**
 * 首页容量页签类型枚举
 */
export enum CapacityTypeE {
    Physical = 'physical',
    Effective = 'effective',
    Subscribed = 'subscribed'
}

/**
 * 所有租户
 */
export const AllVstoreStr = '-1';

/** 给计算型存储返回FC和NVMe的模拟数据 */
export const NVME_OVER_ROCE_RES_COUNT = { data: { COUNT: '0' }, error: { code: 0, description: '0' } };
export const FCPORT_RES_BATCH = { data: [], error: { code: 0, description: '0' } };
/**
 * 没有SmartThin License时 SAN资源返回模拟数据
 */
export const NO_SMART_THIN_RES_COUNT = { data: { COUNT: '0' }, error: { code: 0, description: '0' } };

/**
 * CIFS个数为0数据
 */
export const NO_CIFS_RES_COUNT = { data: { COUNT: '0' }, error: { code: 0, description: '0' } };

@Injectable({ providedIn: 'root' })
export class HomeStrService {
    isEn = this.i18nService.language === 'en';

    deviceInfo = {
        modelLab: this.get('system.hardware_term_diskMode_label'),
        versionLab: this.get('common.device_version_label'),
        patchVersionLab: this.get('home.device_term_patchVersion_lab'),
        snLab: this.get('common.device_ESN_label'),
        criticalScoreTips: this.get('home.device_term_score_40_tip'),
        majorScoreTips: this.get('home.device_term_score_20_tip'),
        warningScoreTips: this.get('home.device_term_score_5_tip'),
        enclosure: this.get('system.hardware_term_chassis_lab'),
        controller: this.get('system.EnumDefine_MOTYPE_CONTROLLER'),
        disk: this.get('monitor.charts_term_objectType_DISK'),
        port: this.get('common.port'),
        power: this.get('common.power_lab'),
        fan: this.get('common.fan_lab'),

        // 极简模式
        healthStatusNum: this.get('home.home_simplifiedMode_healthyRation_lab'),
        lunsLab: this.get('common.LUNs'),
        fileSystemsLab: this.get('common.fileSystem_title'),
        disksLab: this.get('home.home_simplifiedMode_disks_lab'),
        controllersLab: this.get('home.home_simplifiedMode_controllers_lab'),
        portsLab: this.get('home.home_simplifiedMode_ports_lab'),
        enclosuresLab: this.get('home.home_simplifiedMode_enclosures_lab'),
        powersLab: this.get('home.home_simplifiedMode_powers_lab'),
        singlePowerLab: this.get('home.home_simplifiedMode_power_lab'),
        fansLab: this.get('home.home_simplifiedMode_fans_lab'),
        faultLab: this.get('common.EnumDefine_HEALTH_STATUS_fault'),
        healthyLab: this.get('common.EnumDefine_RUNNING_STATUS_E_HEALTHY'),
        goodCondition: (deviceComp: string): string => this.get('home.home_simplifiedMode_goodCondition_info', [deviceComp]),
        alarmInfo: (deviceComp: string): string => this.get('home.home_simplifiedMode_alarm_info', [deviceComp]),
        viewDetailBtn: this.get('system.upgrade_task_start_viewDetail_info'),
        existErrorTips: this.get('common.existError_tip'),
    };

    alarm = {
        title: this.get('common.Alarms'),
        total: this.get('home.alarm_term_total_lab'),

        criticalLab: this.get('common.Main_Label_Urgent'),
        majorLab: this.get('common.Main_Label_Important'),
        warningLab: this.get('common.Main_Label_Warning'),
        moreBtn: this.get('common.more_button') + ' >',
        viewLab: this.get('common.view_value'),
        alarmsLab: this.get('home.home_simplifiedMode_alarms_lab'),
        viewAllAlarms: this.get('home.home_simplifiedMode_viewAllAlarms_btn'),
        recentAlarmsLab: this.get('home.home_simplifiedMode_recentAlarms_lab'),
    };

    anomalyDetectTitle = this.get('monitor.anomalyDetect_task_title');

    capacityUsage = {
        showPoolBtn: this.get('home.viewStoragePool_lab') + ' >',
        title: this.get('common.capacityUsage_column'),
        totalLab: this.get('common.totalCapacity_lab'),
        usedLab: this.get('storage.dashboard_term_CapacityInfo_UsedRawCapacity_label'),
        unusedLab: this.get('storage.dashboard_term_CapacityInfo_UnusedRawCapacity_label'),
        effectiveCapUsageLab: this.get('home.home_simplifiedMode_effectiveCapUsage_lab'),
    };

    capacityExplainTips = this.get('common.common_term_Capacity_normalstatu_msg').replace(/<br\/>/g, '\n');
    physicalCapExplainTips = this.get('home.Capacity_term_physicalCap_tip').replace(/<br\/>/g, '\n');
    effectiveCapExplainTips = this.get('home.Capacity_term_effectiveCap_tip').replace(/<br\/>/g, '\n');
    dailyNewCapacityLab = this.get('home.dataReduction_term_addCap_lab');

    guaranteedCapacity = {
        title: this.get('home.guarantCap_term_tittle'),
        capacityTitle: this.get('common.capacity_column'),
        totalLab: this.capacityUsage.totalLab,
        usedLab: this.capacityUsage.usedLab,
        unusedLab: this.capacityUsage.unusedLab,
        remainingLab: this.get('home.guarantCap_term_remainDays_label'),
        dayLab: this.get('common.lower_day_label'),
        insufficientThresholdLab: this.get('home.guarantCap_term_highAlarmThreshold_label'),
        usedUpThresholdLab: this.get('home.guarantCap_term_lowAlarmThreshold_label'),
        noPermissionTip: this.get('common.NoPermissions_label'),
        lunUsedLab: this.get('common.block_listbox'),
        fsUsedLab: this.get('common.file_listbox'),
    };

    subscribedCapacity = {
        subscribedTitle: this.get('home.Capacity_term_subscribed_tab'),
        physicalTitle: this.get('home.Capacity_term_physical_tab'),
        effectiveTitle: this.get('home.Capacity_term_effective_tab'),
        usedLab: this.capacityUsage.usedLab,
        unusedLab: this.capacityUsage.unusedLab,
        totalLab: this.capacityUsage.totalLab,
        subscribedRateLab: this.get('storage.storagePool_view_task_Total_Subscribed_CapacityRation_label'),
        subscribedRateTip: this.get('home.Capacity_term_subscribedCapRate_tip'),
    };

    capacityTrend = {
        title: this.get('home.FrocastCap_term_title'),
        guaranteedCapacityTitle: this.get('home.guarantCap_term_guarantCapTrend_tittle'),
        tips: this.get('monitor.forecasting_capacity_noSupportForecasting_msg'),
        capacityUsageLab: this.get('common.term_cap_capaRatio_lab'),
        totalLab: this.capacityUsage.totalLab,
        usedLab: this.capacityUsage.usedLab,
        unusedLab: this.capacityUsage.unusedLab,
        moreBtn: this.alarm.moreBtn,
        noPmsLunNoDataTip: this.i18nService.get('home.performance_term_noData_tip'),
        switchStandardTip: this.i18nService.get('home.performance_term_enable_switchStandard_tip'),
        noPmsLunHistoricalDataTip: this.i18nService.get('home.performance_term_enable_historicalData_tip'),
        preiodLab: this.get('common.Punctuation_preiod'),
        colon: this.i18nService.get('common.colon_label'),
        updateTime: this.get('monitor.forecasting_capacity_dataUpdateTime_lab'),
        updateTimeTip: this.i18nService.get('monitor.forecasting_capacity_dataUpdateTime_tip'),
        capUsagePredictionTitle: this.i18nService.get('home.home_simplifiedMode_capUsagePrediction_lab'),
        featureOneWeekLabel: this.i18nService.get('common.nextWeek_listbox'),
        featureOneMonthLabel: this.i18nService.get('common.nextMonth_listbox'),
        featureThreeMonthLabel: this.i18nService.get('common.nextThreemonths_listbox'),
        featureHalfYearLabel: this.i18nService.get('common.nextHalfYear_listbox'),
        featureOneYearLabel: this.i18nService.get('common.nextYear_listbox'),
        allTimeLabel: this.i18nService.get('common.all_value'),
    };

    dataRedunctionCapacity = {
        allValueLab: this.get('common.all_value'),
        blockListLab: this.get('common.block_listbox'),
        fileListLab: this.get('common.file_listbox'),
        capacityTitle: this.get('system.capacity_column'),
        dataRedunctionTitle: this.get('storage.dataReduction_lab'),
        dataReductionRatioLab: this.get('home.dataReduction_term_ratio_lab'),
        dataDeduplicationrateRatioLab: this.get('storage.dashboad_term_datadedupt_label'),
        dataCompressionrateRatioLab: this.get('storage.dashboad_term_compress_label'),
        blockLab: this.get('common.block_listbox'),
        fileLab: this.get('common.file_listbox'),
        preSavingsLab: this.get('home.dataReduction_term_presavings_lab'),
        postSavingsLab: this.get('home.dataReduction_term_postsavings_lab'),
        totalLab: this.capacityUsage.totalLab,
        usedLab: this.capacityUsage.usedLab,
        freeLab: this.capacityUsage.unusedLab,
        unitLab: (unit: string): string => this.get('common.common_unit_lab', [unit]),

        postSavingsTip: this.get('home.dataReduction_term_postsavingsLarger_info_lab'),
        noPermissionTip: this.guaranteedCapacity.noPermissionTip,
        dataRedunctionTips: this.get('storage.dashboad_term_dataReduction_tip'),

        moreBtn: this.alarm.moreBtn,

        // 极简模式
        saveSpaceRatioLab: this.get('home.home_simplifiedMode_saveSpaceRatio_lab'),
        saveSpaceLab: this.get('home.home_simplifiedMode_saveSpace_lab'),
        blockDataLab: this.get('home.home_simplifiedMode_blockData_lab'),
        fsDataLab: this.get('home.home_simplifiedMode_fsData_lab'),
        totalDataLab: this.get('home.home_simplifiedMode_totalData_lab'),
        usedDataLab: this.get('home.home_simplifiedMode_usedData_lab'),
    };

    commonTasks = {
        title: this.get('home.commonTask_term_title'),
        createLunGroupBtn: this.get('home.commonTask_term_createLunGroup_button'),
        createHostBtn: this.get('home.commonTask_term_createHost_button'),
        createLunBtn: this.get('storage.lun_task_create_title'),
        createHostGroupBtn: this.get('host.hostGroup_task_create_hostGroup_button'),
        createPgBtn: this.get('home.commonTask_term_createPG_button'),
        createSnapshotCgBtn: this.get('home.commonTask_term_createSnapshotCG_button'),
        createCloneCgBtn: this.get('home.commonTask_term_createHyperCloneCG_button'),
        createHyperMetroCgBtn: this.get('home.commonTask_term_createhyperMetroCG_button'),
        createReplicationCgBtn: this.get('home.commonTask_term_createReplicationCG_button'),
        createFileSystemBtn: this.get('service.fileSystem_term_create_title'),
        createNfsShareBtn: this.get('service.share_term_createNFS_title'),
        createDtreeBtn: this.get('service.dtree_term_create_title'),

        lunGroupTab: this.get('common.lunGroup_title'),
        hostTab: this.get('common.hosts_title'),
        lunTab: this.get('common.lun_title'),
        hostGroupTab: this.get('common.hostGroups_title'),
        pgTab: this.get('protection.protectionGroup_title'),
        snapshotCgTab: this.get('protection.snapshotCGs_title'),
        replicationCgTab: this.get('protection.replicationCG_title'),
        hyperMetroCgTab: this.get('protection.hyperMetroCGs_title'),
        fileSystemTab: this.get('common.fileSystem_title'),
        nfsShareTab: this.get('common.nfsShare_lab'),
        dtreeTab: this.get('common.dtree_lab'),
    };

    performance = {
        title: this.get('common.dashboard_perf_static_title'),

        lastestfiveMinutesLab: this.get('common.lastestFiveMinute_listbox'),
        lastestHalfanHourLab: this.get('common.lastestHalfanHour_listbox'),
        lastestDayLab: this.get('common.lastestDay_listbox'),
        lastestWeekLab: this.get('common.lastestWeek_listbox'),
        lastestMonthLab: this.get('common.lastestMonth_listbox'),
        lastestYearLab: this.get('common.lastestYear_listbox'),
        readLab: this.get('home.performance_term_read_lab'),
        writeLab: this.get('home.performance_term_write_lab'),
        latencyLab: this.get('home.performance_term_averageRequstTime_title'),
        iopsLab: this.get('storage.smartQoS_term_IOPS_label'),
        bandWidthLab: this.get('home.performance_term_blockBandwidth_title'),
        allLatencyLab: this.get('monitor.performance_term_view_totaLatency_lab'),
        allIopsLab: this.get('monitor.performance_term_view_totalIOPS_lab'),
        allBandWidthLab: this.get('monitor.performance_term_view_totalBandwith_lab'),
        moreBtn: this.capacityTrend.moreBtn,
        noPmsLunNoDataTip: this.i18nService.get('home.performance_term_noData_tip'),
        switchStandardTip: this.i18nService.get('home.performance_term_enable_switchStandard_tip'),
        noPmsLunHistoricalDataTip: this.i18nService.get('home.performance_term_enable_historicalData_tip'),
        preiodLab: this.get('common.Punctuation_preiod'),

        unitConversion: this.get('home.home_term_iops_desc_tip2'),

        system: this.get('common.system_title'),
        block: this.get('common.block_listbox'),
        file: this.get('common.file_label'),
        fileBandWidthLab: this.get('home.performance_term_fileBandwidth_lab'),
        avgLatencyLab: this.get('home.performance_term_AvgOperLatency_lab'),
        opsLab: this.get('home.performance_term_OPS_lab'),

        // 极简模式首页
        top5IOPSTitle: this.get('home.home_simplifiedMode_TopIOPS_lab'),
        top5OPSTitle: this.get('home.home_simplifiedMode_TopOPS_lab'),
        top5CapacityUsageTitle: this.get('home.home_simplifiedMode_TopCapUsage_lab'),

        // 性能码表负载利用率范围
        loadUtilizationLabel: this.get('home.home_term_systemLoad_load_lab'),
        lessThanTenPercent: this.get('home.home_term_systemLoad_lessThanTen_value'),
        tenToTwentyPercent: this.get('home.home_term_systemLoad_tenToTwenty_value'),
        twentyToThirtyPercent: this.get('home.home_term_systemLoad_twentyToThirty_value'),
        thirtyToFortyPercent: this.get('home.home_term_systemLoad_thirtyToForty_value'),
        fortyToFiftyPercent: this.get('home.home_term_systemLoad_fortyToFifty_value'),
        fiftyToSixtyPercent: this.get('home.home_term_systemLoad_fiftyToSixty_value'),
        sixtyToSeventyPercent: this.get('home.home_term_systemLoad_sixtyToSeventy_value'),
        seventyToEightyPercent: this.get('home.home_term_systemLoad_seventyToEighty_value'),
        moreThanEightyPercent: this.get('home.home_term_systemLoad_moreThanEighty_value'),

        // 性能码表指标
        frontPortLabel: this.get('monitor.charts_term_objectType_FRONTPORT_label'),
        backPortLabel: this.get('monitor.charts_term_objectType_BACKPORT'),
        diskLabel: this.get('monitor.charts_term_objectType_DISK'),
        cpuLabel: this.get('home.home_term_systemLoad_cpu_lab'),
        controllerLabel: this.get('monitor.charts_term_objectType_CONTROLLER'),

        slightPauseMarkLab: this.get('common.slightPauseMark_lab'),
        withCommaSpaceMarkLab: this.get('common.comma_withSpace_label'),
        frontPortTipTitle: this.get('home.home_term_systemLoad_frontendport_value'),
        backPortTipTitle: this.get('home.home_term_systemLoad_backendport_value'),
        diskTipTitle: this.get('home.home_term_systemLoad_disk_value'),
        controllerTipTitle: this.get('home.home_term_systemLoad_cpu_value'),
        analysisPageLab: this.get('home.home_term_systemLoad_go_info'),
        goToAnalysisLab: this.get('home.home_term_systemLoad_analysic_link'),

        performanceTableContent: (label: string, value: string): string => this.get('home.home_term_systemLoad_lab', [label, value]),
    };

    overview = {
        overviewTitle: this.get('home.AAOverview_term_AAOverview_tittle'),
        locationLab: this.get('common.location_column'),
        statusLab: this.get('common.status_column'),
        runningStatusLab: this.get('common.runningStatus_column'),
        healthStatusLab: this.get('common.healthStatus_column'),
        usageLab: this.get('monitor.performance_term_indicator_68'),
    };

    topology = {
        noPermissionTip: this.get('common.NoPermissions_label'),
        frontEndCards: this.get('home.AAOverview_term_frontEndInterModules_lab'),
        controllers: this.get('common.controllers'),
        backEndCards: this.get('home.AAOverview_term_backEndInterModules_lab'),
        diskEnclosures: this.get('common.diskEnclousres_tab'),
        deviceTopology: this.get('home.AAOverview_term_deviceTopo_tittle'),
        topoSummary: this.get('home.AAOverview_term_deviceTopo_desc_lab'),
        closeBtn: this.get('common.close_button'),
        expansion: this.get('common.diskEnclousre_lab'),
        control: this.get('common.controllerEnclousre_lab'),
        dataSwitch: this.get('system.EnumDefine_ENCLOSURE_TYPE_E_DSW'),

        restore: this.get('home.AAOverview_term_resetOverview_button'),
        refresh: this.get('system.hardware_term_refresh_button'),
        miniMap: this.get('home.AAOverview_term_overviewMap_lab'),
    };

    network = {
        networkLab: this.get('system.network_tiitle'),
        file: this.get('common.file_listbox'),
        block: this.get('common.block_listbox'),
        port: this.get('common.Ports'),
        physicalPort: this.get('common.physicalPorts_label'),
        lif: this.get('common.LogicalPorts'),
        resource: this.get('home.Home_network_resources_lab'),
        // block
        eth: this.get('common.initializeConfig_term_configStoragePort_eth_lab'),
        roce: this.get('home.Home_network_ROCE_lab'),
        // fc的nvme模式暂不支持，暂不体现nvme
        // fcAndNvme: this.get('home.Home_network_FCNVMe_lab'),
        fcAndNvme: this.get('common.FC_lab'),
        iscsi: this.get('home.Home_network_iSCSI_lab'),
        nvme: this.get('common.NVMeoF_RoCE_lab'),
        lun: this.get('common.LUNs'),
        singleLun: this.get('monitor.charts_term_objectType_LUN'),
        // 复数
        lunGroup: this.get('common.lunGroup_title'),
        host: this.get('common.hosts_title'),
        hostGroup: this.get('common.hostGroups_title'),
        // file
        nfs: this.get('common.NFS_lab'),
        cifs: this.get('common.CIFS_lab'),
        fileSystem: this.get('common.fileSystem_title'),
        singleFileSystem: this.get('service.quota_term_create_FS_lab'),
        Share: this.get('common.share_title'),
        // share
        NfsShare: this.get('common.nfsShare_lab'),
        CifsShare: this.get('common.cifsShare_lab'),
        DataTurbo: this.get('common.DataTurbo_lab'),
        DataTurboOverRoce: this.get('common.DataTurbo_RoCE_lab'),
        NfsOverRdma: this.get('settings.fileStorageService_term_nfsService_NFSoverRDMA_lab'),
    };

    EnclosureLogicTypeE = {
        expansion: {
            value: '0',
            i18n: this.topology.expansion,
        },
        control: {
            value: '1',
            i18n: this.topology.control,
        },
        dataSwitch: {
            value: '2',
            i18n: this.topology.dataSwitch,
        },
    };

    // 性能时间范围
    performanceTimeOptions: SelectOption[] = [
        { label: this.performance.lastestfiveMinutesLab, value: TimeSegmentE.FiveMinutes },
        { label: this.performance.lastestHalfanHourLab, value: TimeSegmentE.HalfHour },
        { label: this.performance.lastestDayLab, value: TimeSegmentE.OneDay },
        { label: this.performance.lastestWeekLab, value: TimeSegmentE.OneWeek },
        { label: this.performance.lastestMonthLab, value: TimeSegmentE.OneMonth },
        { label: this.performance.lastestYearLab, value: TimeSegmentE.OneYear },
    ];

    /**
     * 容量趋势时间
     */
    capacityTimeOptions: SelectOption[] = [
        { label: this.capacityTrend.allTimeLabel, value: CapacityTimeSegmentE.All },
        { label: this.capacityTrend.featureOneWeekLabel, value: CapacityTimeSegmentE.OneWeek },
        { label: this.capacityTrend.featureOneMonthLabel, value: CapacityTimeSegmentE.OneMonth },
        { label: this.capacityTrend.featureThreeMonthLabel, value: CapacityTimeSegmentE.ThreeMonth },
        { label: this.capacityTrend.featureHalfYearLabel, value: CapacityTimeSegmentE.HalfYear },
        { label: this.capacityTrend.featureOneYearLabel, value: CapacityTimeSegmentE.OneYear },
    ];

    performanceTypeOptions: SelectOption[] = [
        { label: this.performance.block, value: ServiceType.BLOCk },
        { label: this.performance.file, value: ServiceType.File },
    ];

    /** 根据时间戳获取昨天日期（年-月-日格式） */
    getYesterdayStr = (timestamp: string | number): string => timestamp ? this.timeService.formatUTC(timestamp, TIME_FORMATTER.DATE) : '';

    dailyNewCapacityTip = (timestamp: string | number): string =>
        this.get('home.dataReduction_term_addCap_tip', [this.getYesterdayStr(timestamp)]);

    constructor(
        private i18nService: I18nService,
        private timeService: TimeService,
    ) { }

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18nService.get(key, params, colon);
    }

    getKey = (key: string, params?: any[] | boolean, colon?: boolean): string => {
        const i18nKey = this.i18nService.get(key, params, colon);
        return i18nKey;
    };

    /**
     * 新告警提示
     * @param num
     */
    getNewAlarmTips(num: number): string {
        if (num > 1) {
            return this.get('home.alarm_term_newOne_lab', [num]);
        } else {
            return this.get('home.alarm_term_newMore_lab', [num]);
        }
    }

    /**
     * 获取括号带变量值
     */
    getBracketVarLab = (value: string | number): string => {
        const str = this.get('common.variable_withBracket_lab', [value]);
        return str;
    };

    /**
     * 获取当日新增容量的显示内容（不需要展示+号）
     * @param originValue 原值
     * @param convertedValue 转换后的值
     */
    getDailyNewCapacityStr = (originValue: number, convertedValue: number | string): string =>
        `${originValue < 0 ? '- ' : ''}${convertedValue}`;
}
